/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { Grid, Typography, Typography2, breakpoints, typography2 } from '@coursera/cds-core';
import type { GridProps } from '@coursera/cds-core';

import Markdown from 'bundles/cms/components/Markdown';
import LazyImgix from 'bundles/page/components/shared/LazyImgix';
import ConversationIcon from 'bundles/unified-description-page-common/components/shared/icons/ConversationIcon';
import InterviewIcon from 'bundles/unified-description-page-common/components/shared/icons/InterviewIcon';
import ResumeIcon from 'bundles/unified-description-page-common/components/shared/icons/ResumeIcon';
import type {
  CareerResourceFragment,
  HiringPartnersFragment,
} from 'bundles/unified-description-page-common/queries/bff/__generated__/SpecializationEntitiesFragments';
import { useMobileOnlyStreamlinedXDP } from 'bundles/unified-description-page-common/utils/useStreamlinedXDP';

import _t from 'i18n!nls/xdp';

const iconsToUse = {
  InterviewIcon,
  ConversationIcon,
  ResumeIcon,
};

type Props = {
  resources?: CareerResourceFragment[] | null;
  hiringPartners?: HiringPartnersFragment | null;
};

const styles = {
  container: css`
    background-color: var(--cds-color-neutral-background-primary-weak);
    border-radius: 8px;
  `,
  resourcesContainer: css`
    padding: var(--cds-spacing-400);
    ${breakpoints.down('xs')} {
      padding: var(--cds-spacing-200);
    }
  `,
  partnersContainer: (showBorder: boolean) => css`
    ${showBorder === true && `border-top: 2px solid var(--cds-color-neutral-disabled);`}
    padding: var(--cds-spacing-400);
    ${breakpoints.down('xs')} {
      padding: var(--cds-spacing-200);
    }
  `,
  title: css`
    ${breakpoints.up('md')} {
      margin-right: var(--cds-spacing-400);
    }
  `,
  resource: css`
    ${breakpoints.down('xs')} {
      display: flex;
    }
  `,
  icon: css`
    margin-bottom: var(--cds-spacing-100);

    ${breakpoints.down('xs')} {
      margin-right: var(--cds-spacing-200);
    }
  `,
  description: css`
    margin-top: var(--cds-spacing-50);

    .rc-Markdown.styled p {
      ${typography2.bodyPrimary};
      margin-bottom: 0;
    }
  `,
  logos: css`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--cds-spacing-600);
    justify-content: space-evenly;
  `,
  logo: css`
    /* override styles added by LazyImgix */
    > div {
      height: auto;
      padding-bottom: unset;
    }

    /* text-align: middle; */
  `,

  mWebResource: css`
    margin: 0 0 var(--cds-spacing-100) 0;
    padding: 0 !important;
    display: flex;
    flex-direction: row;
    align-items: center;
    text-align: center;
  `,
  mWebIcon: css`
    margin-right: var(--cds-spacing-150);
    margin-left: var(--cds-spacing-200);
    margin-bottom: 0;

    svg {
      width: 24px;
      height: 24px;
    }
  `,
  mWebResourceTitle: css`
    margin-top: calc(var(--cds-spacing-50) * -1);
  `,
};

const CareerSupport = ({ resources, hiringPartners }: Props) => {
  const isMobileOnlyStreamlinedXDP = useMobileOnlyStreamlinedXDP();
  const titleColumns = resources && resources.length >= 4 ? 3 : 4;
  const contentColumns = resources && resources.length >= 4 ? 9 : 8;

  if (!resources && !hiringPartners) {
    return null;
  }

  return (
    <div css={styles.container}>
      {resources && resources.length > 0 && (
        <div css={styles.resourcesContainer}>
          <Grid container spacing={32}>
            <Grid item xs={12} md={titleColumns}>
              <Typography variant="h1semibold" component="h3" css={styles.title}>
                {_t('Get exclusive access to career resources upon completion')}
              </Typography>
            </Grid>
            <Grid item container spacing={32} xs={12} md={contentColumns} component="ul">
              {resources.map((resource) => {
                const Icon = iconsToUse[resource.iconName as keyof typeof iconsToUse] || ResumeIcon;

                return (
                  <Grid
                    item
                    component="li"
                    key={resource.title}
                    xs={12}
                    sm={(12 / resources.length) as GridProps['sm']}
                    css={(styles.resource, isMobileOnlyStreamlinedXDP ? styles.mWebResource : '')}
                  >
                    <div css={isMobileOnlyStreamlinedXDP ? styles.mWebIcon : styles.icon}>
                      <Icon />
                    </div>
                    <div css={isMobileOnlyStreamlinedXDP ? styles.mWebResourceTitle : ''}>
                      <Typography2 variant="subtitleMedium" component="div">
                        {resource.title}
                      </Typography2>
                      {resource.description && !isMobileOnlyStreamlinedXDP && (
                        <div css={styles.description}>
                          <Markdown source={resource.description} />
                        </div>
                      )}
                    </div>
                  </Grid>
                );
              })}
            </Grid>
          </Grid>
        </div>
      )}
      {hiringPartners && (
        <div css={styles.partnersContainer(Boolean(resources && resources.length > 0))}>
          <Grid container>
            <Grid item xs={12} md={titleColumns}>
              <Typography2 variant="subtitleMedium" component="h4" css={styles.title}>
                <Markdown source={hiringPartners.title} />
              </Typography2>
            </Grid>
            {hiringPartners.partnerLogos && hiringPartners.partnerLogos.length > 0 && (
              <Grid item xs={12} md={contentColumns} component="ul" css={styles.logos}>
                {hiringPartners.partnerLogos.map((partner) => {
                  return (
                    <li css={styles.logo} key={partner.imageUrl}>
                      <LazyImgix
                        src={partner.imageUrl}
                        alt={partner.imageAltText ?? ''}
                        maxWidth={isMobileOnlyStreamlinedXDP ? 80 : 100}
                        maxHeight={isMobileOnlyStreamlinedXDP ? 40 : 50}
                        enableFluidWidth
                      />
                    </li>
                  );
                })}
              </Grid>
            )}
          </Grid>
        </div>
      )}
    </div>
  );
};

export default CareerSupport;
